<script lang="ts">
    import { onMount } from 'svelte';
    import { getPathParams } from 'svelte-mini-router';
    import { translate } from '@/utils/format';
    import { getSsoSession, savePartnerProfile, performHandoff } from '@/services/sso';

    let partnerId = $state('');
    let partnerName = $state('');
    let partnerLogoUrl = $state('');

    let consentConnect = $state(false);
    let consentMarketing = $state(false);
    let saving = $state(false);
    let handoffDone = $state(false);
    let profileSaved = $state(false);
    let errorMsg = $state('');

    const canHandoff = $derived(consentConnect);

    async function activateHandoff() {
        if (!canHandoff) return;
        saving = true;
        errorMsg = '';

        const session = getSsoSession();

        try {
            if (session?.webToken) {
                await savePartnerProfile(session.webToken, {
                    status: 'profile_saved',
                    consent: {
                        gdpr: true,
                        marketing: consentMarketing,
                        at: Date.now(),
                    },
                });

                const handoff = await performHandoff(session.webToken, partnerId);

                // Copy code to clipboard for iOS PWA fallback
                try {
                    await navigator.clipboard.writeText(`caloo-sso:${handoff.code}`);
                } catch {
                    /* clipboard not available — ignore */
                }

                handoffDone = true;
                saving = false;

                // Open deep link
                setTimeout(() => {
                    window.location.href = handoff.deepLink;
                }, 300);
            } else {
                // No webToken: just mark profile saved, show fallback registration
                profileSaved = true;
                saving = false;
            }
        } catch (err: any) {
            errorMsg = String(err?.message || 'Ha ocurrido un error. Inténtalo de nuevo.');
            saving = false;
        }
    }

    function saveWithoutApp() {
        profileSaved = true;
    }

    onMount(() => {
        const params = getPathParams();
        partnerId = String(params?.['partnerId'] || '').trim();

        const session = getSsoSession();
        partnerName = session?.branding?.partnerName ?? partnerId;
        partnerLogoUrl = session?.branding?.logoUrl ?? '';
    });
</script>

<section class="ssoCmp">
    {#if profileSaved}
        <!-- Profile saved without app -->
        <div class="ssoCmp__saved">
            <div class="ssoCmp__savedIcon">✓</div>
            <h2>{translate('NJO_PROFILE_SAVED', 'Tu perfil está guardado')}</h2>
            <p>
                {translate('NJO_PROFILE_SAVED_DESC', 'Puedes volver a {partner} cuando quieras.', { partner: partnerName })}
            </p>
            <a class="ssoCmp__returnBtn" href="javascript:history.back()">
                {translate('NJO_RETURN_PARTNER', 'Volver a {partner}', { partner: partnerName })}
            </a>
        </div>
    {:else if handoffDone}
        <div class="ssoCmp__handoffDone">
            <div class="ssoCmp__savedIcon">🚀</div>
            <h2>{translate('SSO_HANDOFF_TITLE', 'Abre Caloo para activar tu seguimiento completo')}</h2>
            <p>{translate('SSO_HANDOFF_DESC', 'Hemos guardado tu perfil. La app Caloo te permitirá hacer seguimiento diario, recibir recomendaciones y conectar con {partner}.', { partner: partnerName })}</p>
            <p class="ssoCmp__clipboardNote">{translate('SSO_HANDOFF_CLIPBOARD', 'Código copiado al portapapeles')}</p>
            <button class="ssoCmp__returnGhost" type="button" onclick={saveWithoutApp}>
                {translate('NJO_RETURN_PARTNER', 'Volver a {partner}', { partner: partnerName })}
            </button>
        </div>
    {:else}
        <!-- Header -->
        <header class="ssoCmp__header">
            {#if partnerLogoUrl}
                <img class="ssoCmp__logo" src={partnerLogoUrl} alt={partnerName} />
            {:else}
                <div class="ssoCmp__logoFallback">{partnerName.charAt(0).toUpperCase()}</div>
            {/if}
            <div>
                <h1 class="ssoCmp__partnerName">{partnerName}</h1>
                <p class="ssoCmp__poweredBy">{translate('SSO_POWERED_BY', 'powered by Caloo')}</p>
            </div>
        </header>

        <div class="ssoCmp__body">
            <h2 class="ssoCmp__title">{translate('SSO_CONSENT_TITLE', 'Un último paso')}</h2>
            <p class="ssoCmp__desc">
                {translate('SSO_CONSENT_DESC', 'Antes de activar tu perfil completo, necesitamos tu confirmación:')}
            </p>

            <!-- GDPR consents -->
            <div class="ssoCmp__consents">
                <label class="ssoCmp__consent ssoCmp__consent--required">
                    <input
                        type="checkbox"
                        bind:checked={consentConnect}
                        class="ssoCmp__checkbox"
                    />
                    <span>
                        {translate('NJO_GDPR_CONNECT', 'Conectar mi cuenta de {partner} con Caloo', { partner: partnerName })}
                        <span class="ssoCmp__required">*</span>
                    </span>
                </label>

                <label class="ssoCmp__consent">
                    <input
                        type="checkbox"
                        bind:checked={consentMarketing}
                        class="ssoCmp__checkbox"
                    />
                    <span>
                        {translate('NJO_GDPR_MARKETING', 'Acepto recibir comunicaciones sobre nutrición deportiva')}
                    </span>
                </label>
            </div>

            {#if errorMsg}
                <p class="ssoCmp__error">{errorMsg}</p>
            {/if}

            <button
                class="ssoCmp__cta"
                type="button"
                onclick={activateHandoff}
                disabled={!canHandoff || saving}
            >
                {saving
                    ? translate('SSO_CALCULATING', 'Calculando tu perfil...')
                    : translate('SSO_HANDOFF_CTA', 'Abrir Caloo')}
            </button>

            <button class="ssoCmp__saveOnly" type="button" onclick={saveWithoutApp}>
                {translate('NJO_PROFILE_SAVED', 'Tu perfil está guardado')} — {translate('NJO_RETURN_PARTNER', 'Volver a {partner}', { partner: partnerName })}
            </button>
        </div>
    {/if}

    <!-- Always-visible return button (except on saved screen which has its own) -->
    {#if !profileSaved && !handoffDone}
        <footer class="ssoCmp__footer">
            <a class="ssoCmp__returnLink" href="javascript:history.back()">
                {translate('NJO_RETURN_PARTNER', 'Volver a {partner}', { partner: partnerName })}
            </a>
        </footer>
    {/if}
</section>

<style lang="scss">
    .ssoCmp {
        min-height: 100vh;
        background: var(--backgroundMain);
        color: var(--textColor);
        display: grid;
        align-content: start;
        font-family: 'Montserrat', sans-serif;
        padding: 0 0 40px;
    }

    .ssoCmp__header {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 28px 20px 20px;
        border-bottom: 1px solid var(--border);
    }

    .ssoCmp__logo {
        width: 52px;
        height: 52px;
        border-radius: 14px;
        object-fit: cover;
        border: 1px solid var(--border);
        flex-shrink: 0;
    }

    .ssoCmp__logoFallback {
        width: 52px;
        height: 52px;
        border-radius: 14px;
        background: var(--partner-primary, var(--primary));
        color: #12121f;
        display: grid;
        place-items: center;
        font-size: 22px;
        font-weight: 900;
        flex-shrink: 0;
    }

    .ssoCmp__partnerName {
        margin: 0;
        font-size: 18px;
        font-weight: 900;
        color: var(--black);
        letter-spacing: -0.01em;
        line-height: 1.2;
    }

    .ssoCmp__poweredBy {
        margin: 4px 0 0;
        font-size: 11px;
        color: var(--subtleText);
        opacity: 0.6;
        letter-spacing: 0.03em;
    }

    .ssoCmp__body {
        padding: 24px 20px;
        display: grid;
        gap: 16px;
    }

    .ssoCmp__title {
        margin: 0;
        font-size: 22px;
        font-weight: 900;
        color: var(--black);
        letter-spacing: -0.02em;
        line-height: 1.2;
    }

    .ssoCmp__desc {
        margin: 0;
        font-size: 14px;
        color: var(--subtleText);
        line-height: 1.45;
    }

    .ssoCmp__consents {
        display: grid;
        gap: 12px;
        padding: 16px;
        border: 1px solid var(--border);
        border-radius: 14px;
        background: var(--white);
    }

    .ssoCmp__consent {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        cursor: pointer;
        font-size: 13px;
        color: var(--textColor);
        line-height: 1.45;
    }

    .ssoCmp__consent--required {
        font-weight: 700;
    }

    .ssoCmp__checkbox {
        width: 18px;
        height: 18px;
        border-radius: 5px;
        accent-color: var(--partner-primary, var(--primary));
        flex-shrink: 0;
        margin-top: 1px;
        cursor: pointer;
    }

    .ssoCmp__required {
        color: var(--partner-primary, var(--primary));
        font-weight: 900;
        margin-left: 2px;
    }

    .ssoCmp__error {
        margin: 0;
        font-size: 13px;
        color: #e55;
        background: rgba(255, 80, 80, 0.06);
        border: 1px solid rgba(255, 80, 80, 0.2);
        border-radius: 10px;
        padding: 10px 14px;
        line-height: 1.4;
    }

    .ssoCmp__cta {
        width: 100%;
        padding: 16px 24px;
        border: none;
        border-radius: 14px;
        background: linear-gradient(180deg, #5aeaa0 0%, var(--partner-primary, var(--primary)) 100%);
        color: #12121f;
        font-size: 15px;
        font-weight: 900;
        font-family: inherit;
        cursor: pointer;
        letter-spacing: -0.01em;
        box-shadow: 0 4px 14px rgba(76, 227, 157, 0.25);
        transition: opacity 0.15s, transform 0.12s;
    }

    .ssoCmp__cta:hover:not(:disabled) {
        opacity: 0.9;
        transform: translateY(-1px);
    }

    .ssoCmp__cta:active:not(:disabled) {
        transform: scale(0.98);
    }

    .ssoCmp__cta:disabled {
        opacity: 0.45;
        cursor: not-allowed;
        background: var(--border);
        color: var(--subtleText);
        box-shadow: none;
    }

    .ssoCmp__saveOnly {
        background: transparent;
        border: none;
        padding: 10px 0;
        font-size: 13px;
        color: var(--subtleText);
        font-family: inherit;
        cursor: pointer;
        text-align: center;
        line-height: 1.4;
    }

    .ssoCmp__saveOnly:hover {
        color: var(--black);
    }

    /* Saved state */
    .ssoCmp__saved,
    .ssoCmp__handoffDone {
        display: grid;
        gap: 14px;
        justify-items: center;
        text-align: center;
        padding: 60px 20px 40px;
    }

    .ssoCmp__savedIcon {
        font-size: 52px;
        line-height: 1;
    }

    .ssoCmp__saved h2,
    .ssoCmp__handoffDone h2 {
        margin: 0;
        font-size: 22px;
        font-weight: 900;
        color: var(--black);
        letter-spacing: -0.02em;
        line-height: 1.25;
        max-width: 320px;
    }

    .ssoCmp__saved p,
    .ssoCmp__handoffDone p {
        margin: 0;
        font-size: 14px;
        color: var(--subtleText);
        line-height: 1.45;
        max-width: 340px;
    }

    .ssoCmp__clipboardNote {
        font-size: 12px !important;
        opacity: 0.6;
    }

    .ssoCmp__returnBtn {
        padding: 14px 24px;
        border-radius: 14px;
        background: var(--partner-primary, var(--primary));
        color: #12121f;
        font-size: 14px;
        font-weight: 900;
        font-family: inherit;
        text-decoration: none;
        display: inline-block;
        margin-top: 8px;
    }

    .ssoCmp__returnGhost {
        background: transparent;
        border: 1px solid var(--border);
        padding: 12px 20px;
        border-radius: 12px;
        font-size: 13px;
        font-weight: 700;
        color: var(--subtleText);
        font-family: inherit;
        cursor: pointer;
    }

    /* Footer */
    .ssoCmp__footer {
        padding: 0 20px;
        text-align: center;
        border-top: 1px solid var(--border);
        margin-top: 8px;
        padding-top: 16px;
    }

    .ssoCmp__returnLink {
        font-size: 13px;
        font-weight: 700;
        color: var(--subtleText);
        text-decoration: none;
    }

    .ssoCmp__returnLink:hover {
        color: var(--black);
        text-decoration: underline;
    }
</style>
